
import { useState, useCallback } from 'react';
import { Quest, QuestCategory } from '../types';
import { ALL_QUESTS, CATEGORY_ORDER } from '../constants';

export const useQuestState = (initialState: {
  quests: Quest[];
  rejectedQuests: Quest[];
  activeQuests: Quest[];
  offeredQuests: { [key: string]: Quest | null };
  completedQuestIds: number[];
  lastDrawnQuestIds: { [key: string]: number };
}) => {
  const [quests, setQuests] = useState(initialState.quests);
  const [rejectedQuests, setRejectedQuests] = useState(initialState.rejectedQuests);
  const [activeQuests, setActiveQuests] = useState(initialState.activeQuests);
  const [offeredQuests, setOfferedQuests] = useState(initialState.offeredQuests);
  const [completedQuestIds, setCompletedQuestIds] = useState(initialState.completedQuestIds);
  const [lastDrawnQuestIds, setLastDrawnQuestIds] = useState(initialState.lastDrawnQuestIds);

  const drawQuestForCategory = useCallback((category: QuestCategory) => {
    const activeQuestIds = activeQuests.map(q => q.id);
    const allQuestsForCategory = quests.filter(q => q.category === category && !activeQuestIds.includes(q.id));

    const unlockedQuestlineQuests = allQuestsForCategory.filter(q => 
        q.prerequisiteQuestId &&
        completedQuestIds.includes(q.prerequisiteQuestId) &&
        !completedQuestIds.includes(q.id)
    );

    if (unlockedQuestlineQuests.length > 0) {
        const newQuest = unlockedQuestlineQuests[0];
        setOfferedQuests(prev => ({ ...prev, [category]: newQuest }));
        setLastDrawnQuestIds(prev => ({ ...prev, [category]: newQuest.id }));
        return;
    }

    const lastDrawnId = lastDrawnQuestIds[category];
    const normalQuestPool = allQuestsForCategory.filter(q => 
        !q.prerequisiteQuestId &&
        q.id !== lastDrawnId
    );
    
    const questPool = normalQuestPool.length > 0
        ? normalQuestPool
        : allQuestsForCategory.filter(q => !q.prerequisiteQuestId);

    if (questPool.length === 0) {
        setOfferedQuests(prev => ({ ...prev, [category]: null }));
        return;
    }

    const newQuest = questPool[Math.floor(Math.random() * questPool.length)];
    setOfferedQuests(prev => ({ ...prev, [category]: newQuest }));
    setLastDrawnQuestIds(prev => ({ ...prev, [category]: newQuest.id }));
  }, [quests, activeQuests, lastDrawnQuestIds, completedQuestIds]);
  
  const handleChooseQuest = useCallback((quest: Quest) => {
    setActiveQuests(prev => [...prev, quest]);
    drawQuestForCategory(quest.category);
  }, [drawQuestForCategory]);

  const handleRejectQuest = useCallback((questToReject: Quest) => {
    setQuests(prevQuests => prevQuests.filter(q => q.id !== questToReject.id));
    setRejectedQuests(prevRejected => [...prevRejected, questToReject]);
    drawQuestForCategory(questToReject.category);
  }, [drawQuestForCategory]);

  const handleRestoreQuest = useCallback((questToRestore: Quest) => {
    setRejectedQuests(prev => prev.filter(q => q.id !== questToRestore.id));
    setQuests(prev => [...prev, questToRestore]);
  }, []);

  const handleResetQuestline = useCallback((questlineId: string) => {
    const questIdsToReset = ALL_QUESTS
      .filter(q => q.questlineId === questlineId)
      .map(q => q.id);
    
    setCompletedQuestIds(prev => prev.filter(id => !questIdsToReset.includes(id)));
    
    CATEGORY_ORDER.forEach(drawQuestForCategory);

  }, [drawQuestForCategory]);

  return {
    quests,
    rejectedQuests,
    activeQuests,
    offeredQuests,
    completedQuestIds,
    lastDrawnQuestIds,
    setQuests,
    setRejectedQuests,
    setActiveQuests,
    setOfferedQuests,
    setCompletedQuestIds,
    setLastDrawnQuestIds,
    drawQuestForCategory,
    handleChooseQuest,
    handleRejectQuest,
    handleRestoreQuest,
    handleResetQuestline,
  };
};
